import React, { useEffect, useMemo } from "react";
import { isEmpty, sum, orderBy } from "lodash";
import { useContentViewsContext } from "../../ContentViewsContext";
import ChartTitle from "components/ChartTitle";
import { GRID_GUTTER, LABEL_INACTIVE } from "constants/ui";
import { Card, Col, Row, Space, Typography } from "antd";
import TableChart from "components/charts/TableChart";
import { DIMENSION_ITEM_SOURCE } from "constants/customDimensions";
import { METRIC_EVENT_COUNT } from "constants/metrics";
import {
  DIMENSION_DATE,
  DIMENSION_EVENT_NAME,
  DIMENSION_HOSTNAME,
} from "constants/dimensions";
import { EVENT_VIEW_ITEM_IMPRESSION } from "constants/customEvents";
import {
  SOURCE_LIGHTWEIGHT,
  SOURCE_MOBILE,
  SOURCE_PLUGILO,
  SOURCE_PLUGIT,
  SOURCE_WIDGET,
} from "constants/contentSources";

import LineChart from "components/charts/LineChart";
import { reviseTimeSeriesData } from "services/gaService";
import { useFormatNumber } from "hooks/useFormatNumber";

function Impressions() {
  const { summaryReport, impressionsReport, dateStrings, setTimeSeriesData } =
    useContentViewsContext();

  const formatNumber = useFormatNumber();

  const impressionsByPartnersTableData = useMemo(() => {
    if (isEmpty(impressionsReport)) return [];

    const rowsGroupedByPartner = impressionsReport.rows.reduce(
      (result, current) => {
        const hostName = current[DIMENSION_HOSTNAME];
        if (!result[hostName]) result[hostName] = {};
        result[hostName][DIMENSION_HOSTNAME] = hostName;
        result[hostName][METRIC_EVENT_COUNT] = sum([
          result[hostName][METRIC_EVENT_COUNT],
          current[METRIC_EVENT_COUNT],
        ]);
        return result;
      },
      {}
    );

    return orderBy(
      Object.values(rowsGroupedByPartner),
      [METRIC_EVENT_COUNT],
      ["desc"]
    );
  }, [impressionsReport]);

  const impressionsTrendTimeSeriesData = useMemo(() => {
    if (isEmpty(summaryReport)) return [];

    const dataKeysMap = {};
    const rowsGroupedByDate = summaryReport.rows
      .filter(
        (row) =>
          [EVENT_VIEW_ITEM_IMPRESSION].includes(row[DIMENSION_EVENT_NAME]) &&
          [
            SOURCE_WIDGET,
            SOURCE_PLUGILO,
            SOURCE_LIGHTWEIGHT,
            SOURCE_PLUGIT,
            SOURCE_MOBILE,
          ].includes(row[DIMENSION_ITEM_SOURCE])
      )

      .reduce((result, current) => {
        const date = current[DIMENSION_DATE];
        const source = current[DIMENSION_ITEM_SOURCE];
        if (!result[date]) result[date] = {};

        result[date][DIMENSION_DATE] = date;

        result[date][source] = sum([
          result[date][source],
          current[METRIC_EVENT_COUNT],
        ]);
        result[date][METRIC_EVENT_COUNT] = sum([
          result[date][METRIC_EVENT_COUNT],
          current[METRIC_EVENT_COUNT],
        ]);

        dataKeysMap[source] = source;
        return result;
      }, {});

    const series = reviseTimeSeriesData(Object.values(rowsGroupedByDate), {
      dateKey: DIMENSION_DATE,
      startDate: dateStrings[0],
      endDate: dateStrings[1],
      dataKeys: [...Object.keys(dataKeysMap), METRIC_EVENT_COUNT],
    });
    return series.map((x) => ({
      ...x,
      [`${SOURCE_PLUGILO}-${SOURCE_LIGHTWEIGHT}`]:
        (x[SOURCE_PLUGILO] || 0) + (x[SOURCE_LIGHTWEIGHT] || 0),
      [`${SOURCE_WIDGET}-${SOURCE_PLUGIT}`]:
        (x[SOURCE_WIDGET] || 0) + (x[SOURCE_PLUGIT] || 0),
      [SOURCE_MOBILE]: x[SOURCE_MOBILE] || 0,
    }));
  }, [dateStrings, summaryReport]);
  // console.log("impressionsTrendTimeSeriesData", impressionsTrendTimeSeriesData);

  useEffect(() => {
    setTimeSeriesData("impressions", impressionsTrendTimeSeriesData);
  }, [impressionsTrendTimeSeriesData]);

  return (
    <section className="mt-8 break-inside-avoid">
      <Space className="mb-2">
        <span className="text-xl font-semibold mb-0">Impressions</span> (plugilo
        & Widgets)
      </Space>
      <Row gutter={GRID_GUTTER}>
        <Col xs={24} lg={12}>
          <Card size="small" className="lg:h-full flex-grow">
            <TableChart
              rowKey={DIMENSION_HOSTNAME}
              dataSource={impressionsByPartnersTableData}
              columns={[
                {
                  title: "Partner",
                  dataIndex: DIMENSION_HOSTNAME,
                  key: DIMENSION_HOSTNAME,
                  ellipsis: true,
                },
                {
                  title: "Impressions",
                  dataIndex: METRIC_EVENT_COUNT,
                  key: METRIC_EVENT_COUNT,
                  width: 100,
                  align: "right",
                  render: (value) => formatNumber(value),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <Card size="small" className="mt-8">
        <LineChart
          xAxisDataKey="date"
          data={impressionsTrendTimeSeriesData}
          lines={[
            {
              name: "plugilo",
              dataKey: `${SOURCE_PLUGILO}-${SOURCE_LIGHTWEIGHT}`,
            },
            {
              name: "Widgets",
              dataKey: `${SOURCE_WIDGET}-${SOURCE_PLUGIT}`,
            },

            {
              name: "Mobile",
              dataKey: SOURCE_MOBILE,
            },
          ]}
        />

        {!isEmpty(impressionsTrendTimeSeriesData) && (
          <p className="text-center mt-4">
            <span className="text-lg">Impressions </span>
            (plugilo & Widgets)
          </p>
        )}
      </Card>
    </section>
  );
}

Impressions.propTypes = {};

export default Impressions;
